import { useState, useEffect } from 'react';

/**
 * Hook to detect when a new app version is available
 * Checks every 5 minutes by fetching a version.json file
 * Returns { hasUpdate, checkNow, dismissUpdate }
 */
export function useVersionCheck() {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);

  const checkForUpdates = async () => {
    try {
      // Fetch version.json with cache-busting
      const response = await fetch(`/version.json?t=${Date.now()}`);
      if (!response.ok) return;
      
      const data = await response.json();
      const serverVersion = data.version;

      // First load: store the version
      if (!currentVersion) {
        setCurrentVersion(serverVersion);
        localStorage.setItem('app_version', serverVersion);
        return;
      }

      // Check if version changed
      if (serverVersion !== currentVersion) {
        console.log(`🎉 New version available: ${serverVersion} (current: ${currentVersion})`);
        setHasUpdate(true);
      }
    } catch (err) {
      // Silently fail - don't bother users with version check errors
      console.debug('Version check failed:', err);
    }
  };

  useEffect(() => {
    // Load stored version on mount
    const stored = localStorage.getItem('app_version');
    if (stored) {
      setCurrentVersion(stored);
    }

    // Check immediately
    checkForUpdates();

    // Check every 5 minutes
    const interval = setInterval(checkForUpdates, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentVersion]);

  const checkNow = () => {
    checkForUpdates();
  };

  const dismissUpdate = () => {
    setHasUpdate(false);
  };

  return { hasUpdate, checkNow, dismissUpdate };
}
